// @flow

import React, {useState, useEffect, useRef} from "react";
import {
    Col,
    Row,
    Table,
    Form,
    Input,
    InputNumber,
    Switch,
    Button,
    Checkbox,
    Radio,
    Tooltip,
    Select
} from "antd";
import {LockTwoTone, UnlockTwoTone} from "@ant-design/icons";

import {useFormik, FieldArray, FormikProvider, getIn} from "formik";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {arrayAlphabeticalSort, handleCheckBoxText} from "../../../utils/helpers";
import {cities} from "../../../constants/constants";
import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {
    CompanyContanier,
    CompanyButtonContainer,
    SubCompanyContainer,
    FilterContainer,
    SelectField,
    DeliveryType,
    DeliveryTypeItem,
    DeliveryTypeLabel,
    DeliveryTypeWarning
} from "./optionsStyles";
import {SaveOutlined} from "@ant-design/icons";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const {Column} = Table;

export const ProofOfDelivery = ({companyId}) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation(["proofOfDelivery"]);

    const [currentPage, setCurrentPage] = useState(1);
    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);
    const [isCompanyDeliveryTypeChanged, setIsCompanyDeliveryTypeChanged] = useState(false);
    const [companyFormData, setCompanyFormData] = useState();
    const [city, setCity] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [text, setText] = useState("");
    const [showSubmitButton, setShowSubmitButton] = useState({});
    const [isSubCompanySubmitting, setIsSubCompanySubmitting] = useState(false);
    const [companyRecall, setCompanyRecall] = useState(false);
    const [subCompanyRecall, setSubCompanyRecall] = useState(false);
    const [upperGroup, setUpperGroup] = useState("");
    const [regionGroups, setRegionGroups] = useState([]);
    const [regionGroupsNames, setRegionGroupsNames] = useState([]);
    const [deliveryType, setDeliveryType] = useState();
    const [documentCount, setDocumentCount] = useState();
    const companyRecallRef = useRef(companyRecall);
    companyRecallRef.current = companyRecall;
    const subCompanyRecallRef = useRef(subCompanyRecall);
    subCompanyRecallRef.current = subCompanyRecall;

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: false,
            vehicleType: null
        }),
        dependencies: [companyRecall]
    });

    const [subCompanyLoading, subCompanyData, setSubCompanyData, setSubCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn:
            companyData.data && companyData.data._id
                ? stores.companyStore.getSubCompanyList.bind(null, {
                      companyId: companyData.data._id,
                      limit: 10,
                      skip: (currentPage - 1) * 10,
                      activity: "enabled",
                      vehicleType: null,
                      city,
                      searchText,
                      upperGroup: upperGroup,
                      regionGroups: regionGroups?.join(".")
                  })
                : () => [],
        dependencies: [
            companyData,
            currentPage,
            subCompanyRecall,
            city,
            searchText,
            upperGroup,
            regionGroups
        ]
    });

    const [upperGroupsLoading, upperGroups, setUpperGroups, setUpperGroupsLoading] = useFetch({
        fetchFn: stores.regionStore.getUpperGroupsByCompanyIdAndCity.bind(null, {
            companyId,
            city
        }),
        dependencies: [companyId, city],
        initialCall: false
    });

    const formik = useFormik({
        initialValues: {
            options: {
                proof: {
                    isActive: companyData?.data?.options?.proof?.isActive,
                    applyProofOptions: false,
                    labels: [
                        {
                            isHidden: false,
                            name: ""
                        }
                    ]
                }
            },
            ...(companyData && companyData.data)
        },
        enableReinitialize: true,
        validateOnChange: true,
        onSubmit: async values => {
            setCompanyLoading(true);

            const hasLabels = values.options.proof?.labels.filter(label => label?.name);

            if (deliveryType === "delivery") {
                values.options.proof.labels = [{name: "photo", isHidden: false}];
            } else if (values.options.proof.labels.length > documentCount) {
                values.options.proof.labels.length = documentCount;
            } else if (hasLabels.length !== documentCount) {
                stores.appStore.openErrorModal({
                    title: `${t("modal.errorTitle", {ns: "common"})}!`,
                    content: t("proofOfDelivery.validation.fillAllTitles", {ns: "proofOfDelivery"})
                });
                setCompanyLoading(false);
                return;
            }

            values.options.proof.labels.map(label => {
                if (label?.name && !label?.isHidden) {
                    return (label.isHidden = false);
                }
            });

            let filters = {};
            filters.searchText = searchText;
            filters.city = city;
            filters.upperGroup = upperGroup;
            filters.regionGroups = regionGroups;
            if (Object.keys(filters).length > 0) {
                values.filters = filters;
            }

            if (!!values?.options?.barcode?.isActive && !!values?.options?.proof?.isActive) {
                stores.appStore.openConfirmModal({
                    title: t("modal.confirmTitle", {ns: "common"}),
                    content: t("other.barcodeOverrideConfirmContent", {
                        ns: "common"
                    }),
                    onOk: async () => {
                        await deactivateCompanyBarcodeOptions(values);
                        await updateCompanyProofOfDeliveryOptions(values);
                    },
                    onCancel: () => setCompanyLoading(false)
                });
            } else {
                await updateCompanyProofOfDeliveryOptions(values);
            }
        }
    });

    const updateCompanyProofOfDeliveryOptions = async values => {
        await stores.companyStore.updateCompanyProofOfDelivery(values);
        if (values.isActive) {
            setSubCompanyRecall(!subCompanyRecallRef.current);
        }
        setCompanyRecall(!companyRecallRef.current);
        setCompanyLoading(false);
        setIsCompanyFormChanged(false);
        return;
    };

    const deactivateCompanyBarcodeOptions = async values => {
        values.options.barcode.isActive = false;
        await stores.companyStore.updateCompanyBarcodeOptions(values);
        return;
    };

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        if (values && !isCompanyFormChanged) {
            if (companyData?.data) {
                setCompanyFormData(values);
                if (values.options.proof?.labels?.[0].name !== "photo") {
                    setDeliveryType("document");
                    if (!documentCount) {
                        setDocumentCount(values.options.proof.labels?.length);
                    }
                    setFieldValue("options.proof.labels", values.options.proof.labels);
                } else {
                    setDeliveryType("delivery");
                }
            }
        } else if (values && JSON.stringify(companyFormData) !== JSON.stringify(values)) {
            setIsCompanyFormChanged(true);
        } else {
            setIsCompanyFormChanged(false);
        }
    }, [values]);

    useEffect(() => {
        if (documentCount && documentCount !== values.options.proof.labels?.length)
            setIsCompanyDeliveryTypeChanged(true);
        else setIsCompanyDeliveryTypeChanged(false);
    }, [documentCount]);

    useEffect(() => {
        if (companyData?.data) {
            if (
                (values.options.proof?.labels?.[0]?.name === "photo" &&
                    deliveryType !== "delivery") ||
                (values.options.proof?.labels?.[0]?.name !== "photo" && deliveryType !== "document")
            ) {
                setIsCompanyDeliveryTypeChanged(true);
            } else {
                setIsCompanyDeliveryTypeChanged(false);
            }
        }
    }, [deliveryType]);

    const onSubCompanySubmit = async index => {
        setIsSubCompanySubmitting(true);
        let submitData = subCompanyData.rows[index]?.options?.proof;
        submitData.name = subCompanyData.rows[index]?.name;
        submitData._id = subCompanyData.rows[index]?._id;
        const params = {
            subCompanyId: submitData._id,
            options: {
                labels: submitData.labels,
                isActive: submitData.isActive,
                requiredStep: submitData.requiredStep,
                requiredType: submitData.requiredType
            }
        };
        const result = await stores.companyStore.updateSubCompanyProofOfDelivery(params);
        if (result) {
            setSubCompanyRecall(!subCompanyRecallRef.current);
        }
        setIsSubCompanySubmitting(false);
    };

    return (
        <div data-testid="podScreen">
            <h2 data-testid="companyName">{companyData?.data?.companyName}</h2>
            <CompanyContanier>
                <Form
                    autoComplete="off"
                    data-testid="podForm"
                    onFinish={handleSubmit}
                    onChange={e => setIsCompanyFormChanged(true)}
                    labelCol={{span: 24}}
                    wrapperCol={{span: 24}}
                >
                    <Table
                        loading={companyLoading}
                        dataSource={[companyData.data]}
                        rowKey="_id"
                        pagination={false}
                        data-testid="podTable"
                        locale={{
                            emptyText: <EmptyMessage />
                        }}
                    >
                        <ErrorFocus formik={formik} />
                        <FormikProvider value={formik}>
                            <Column
                                title={`${t("proofOfDelivery.fields.proofOfDelivery.label", {
                                    ns: "proofOfDelivery"
                                })} (${t("proofOfDelivery.fields.proofOfDelivery.short", {
                                    ns: "proofOfDelivery"
                                })})`}
                                dataIndex="isActive"
                                render={() => (
                                    <Form.Item>
                                        <Switch
                                            checked={values.options?.proof?.isActive}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            data-testid="podIsActive"
                                            onChange={e => {
                                                setFieldValue("options.proof.isActive", e);
                                                setIsCompanyFormChanged(true);
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="left"
                            />
                            <Column
                                title={t("proofOfDelivery.fields.requiredStep", {
                                    ns: "proofOfDelivery"
                                })}
                                dataIndex="requiredStep"
                                render={() => (
                                    <Form.Item data-testid="requiredStepRadioGroup">
                                        <Radio.Group
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.proof.requiredStep",
                                                    e.target.value
                                                );
                                                setIsCompanyFormChanged(true);
                                            }}
                                            value={values.options?.proof?.requiredStep}
                                        >
                                            <Radio
                                                value={"preTrip"}
                                                data-testid="requiredStepRadio"
                                            >
                                                {t("other.pickup", {ns: "common"})}
                                            </Radio>
                                            <Radio
                                                value={"postTrip"}
                                                data-testid="requiredStepRadio"
                                            >
                                                {t("other.delivery", {ns: "common"})}
                                            </Radio>
                                            <Radio value={"both"} data-testid="requiredStepRadio">
                                                {t("filters.all", {ns: "common"})}
                                            </Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                )}
                                align="left"
                            />
                            <Column
                                title={t("proofOfDelivery.fields.requiredType", {
                                    ns: "proofOfDelivery"
                                })}
                                dataIndex="requiredType"
                                render={() => (
                                    <Form.Item data-testid="requiredTypeRadioGroup">
                                        <Radio.Group
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.proof.requiredType",
                                                    e.target.value
                                                );
                                                setIsCompanyFormChanged(true);
                                            }}
                                            value={values.options?.proof?.requiredType}
                                        >
                                            <Radio
                                                value={"delivery"}
                                                data-testid="requiredTypeRadio"
                                            >
                                                {t("deliveryTypes.delivery", {ns: "common"})}
                                            </Radio>
                                            <Radio value={"return"} data-testid="requiredTypeRadio">
                                                {t("deliveryTypes.return", {ns: "common"})}
                                            </Radio>
                                            <Radio value={"both"} data-testid="requiredTypeRadio">
                                                {t("filters.all", {ns: "common"})}
                                            </Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                )}
                                align="left"
                            />
                        </FormikProvider>
                    </Table>

                    <Row>
                        <Form.Item>
                            <DeliveryType>
                                <DeliveryTypeLabel>
                                    {t("proofOfDelivery.fields.deliveryType", {
                                        ns: "proofOfDelivery"
                                    })}
                                </DeliveryTypeLabel>
                                <Select
                                    showSearch
                                    name="deliveryType"
                                    data-testid="deliveryTypeSelect"
                                    value={deliveryType}
                                    onChange={value => {
                                        setDeliveryType(value);
                                    }}
                                >
                                    <Select.Option
                                        data-testid="deliveryTypeSelect_option"
                                        key={"delivery"}
                                        value={"delivery"}
                                    >
                                        {t("proofOfDelivery.fields.normalOrder", {
                                            ns: "proofOfDelivery"
                                        })}
                                    </Select.Option>
                                    <Select.Option
                                        data-testid="deliveryTypeSelect_option"
                                        key={"document"}
                                        value={"document"}
                                    >
                                        {t("proofOfDelivery.fields.documentDelivery", {
                                            ns: "proofOfDelivery"
                                        })}
                                    </Select.Option>
                                </Select>
                            </DeliveryType>
                        </Form.Item>
                        {deliveryType === "document" ? (
                            <>
                                <DeliveryTypeItem span={3}>
                                    <Form.Item>
                                        <DeliveryTypeLabel>
                                            {t("proofOfDelivery.fields.documentCount", {
                                                ns: "proofOfDelivery"
                                            })}
                                        </DeliveryTypeLabel>
                                        <Select
                                            name="documentCount"
                                            data-testid="documentCountSelect"
                                            value={documentCount}
                                            onChange={value => {
                                                setDocumentCount(value);
                                            }}
                                        >
                                            {[1, 2, 3, 4, 5].map(number => (
                                                <Select.Option
                                                    key={number}
                                                    value={number}
                                                    data-testid="documentCountSelect_option"
                                                >
                                                    {number}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </DeliveryTypeItem>
                            </>
                        ) : null}
                    </Row>
                    {deliveryType === "document" ? (
                        <Row data-testid="documentInformationCard">
                            {[...Array(documentCount)].map((count, i) => (
                                <DeliveryTypeItem
                                    data-testid="documentInformation"
                                    data-testvalue={i}
                                >
                                    <Form.Item>
                                        <Row align="middle">
                                            <Col>
                                                <DeliveryTypeLabel>
                                                    {i +
                                                        1 +
                                                        ". " +
                                                        t("proofOfDelivery.fields.proofLabel", {
                                                            ns: "proofOfDelivery"
                                                        })}
                                                </DeliveryTypeLabel>
                                                <Input
                                                    value={getIn(
                                                        values,
                                                        `options.proof.labels.${i}.name`
                                                    )}
                                                    name="label"
                                                    data-testid="documentLabelInput"
                                                    onChange={async event => {
                                                        setFieldValue(
                                                            `options.proof.labels.${i}.name`,
                                                            event.target.value
                                                        );
                                                    }}
                                                />
                                            </Col>
                                            <Col>
                                                <Button
                                                    data-testid="documentHiddenButton"
                                                    style={{paddingLeft: 5, paddingRight: 0}}
                                                    onClick={() => {
                                                        setIsCompanyFormChanged(true);
                                                        setFieldValue(
                                                            `options.proof.labels.${i}.isHidden`,
                                                            !values.options.proof.labels?.[i]
                                                                ?.isHidden
                                                        );
                                                    }}
                                                    type="link"
                                                >
                                                    {values.options.proof.labels?.[i]?.isHidden ? (
                                                        <>
                                                            <LockTwoTone twoToneColor="red" />
                                                            <div style={{color: "red"}}>
                                                                {t(
                                                                    "proofOfDelivery.fields.hidden",
                                                                    {ns: "proofOfDelivery"}
                                                                )}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <UnlockTwoTone twoToneColor="#00b300" />
                                                            <div style={{color: "#00b300"}}>
                                                                {t("proofOfDelivery.fields.open", {
                                                                    ns: "proofOfDelivery"
                                                                })}
                                                            </div>
                                                        </>
                                                    )}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Form.Item>
                                </DeliveryTypeItem>
                            ))}
                        </Row>
                    ) : null}
                    <DeliveryTypeWarning>
                        {t("proofOfDelivery.warning.deliveryType", {ns: "proofOfDelivery"})}
                    </DeliveryTypeWarning>
                    <CompanyButtonContainer justify="end" align="middle">
                        <Checkbox
                            data-testid="applyForAllSubCompaniesCheckbox"
                            checked={values?.applyProofOptions}
                            onChange={e => setFieldValue("applyProofOptions", e.target.checked)}
                        >
                            {handleCheckBoxText(upperGroup, regionGroupsNames, searchText, city)}
                        </Checkbox>
                        <Button
                            htmlType="submit"
                            data-testid="submitButton"
                            disabled={
                                (!isCompanyFormChanged && !isCompanyDeliveryTypeChanged) ||
                                isSubmitting
                            }
                            type="primary"
                        >
                            {t("actions.save", {ns: "common"})}
                        </Button>
                    </CompanyButtonContainer>
                </Form>
            </CompanyContanier>
            <Col span={12} data-testid="filterCard">
                <FilterContainer type="flex" gutter={[20, 20]}>
                    <Col span={6}>
                        <CityFilter
                            value={city}
                            onChange={value => {
                                setCurrentPage(1);
                                setCity(value);
                                setRegionGroups([]);
                                setRegionGroupsNames([]);
                            }}
                        />
                    </Col>
                    <Col span={6}>
                        <h5>{t("fields.upperGroup", {ns: "common"})}</h5>
                        <SelectField
                            value={upperGroup}
                            onChange={value => {
                                setCurrentPage(1);
                                setUpperGroup(value);
                            }}
                            disabled={!city}
                            notFoundContent={t("filters.dataNotFound", {ns: "common"})}
                            placeholder={t("filters.addFilter", {ns: "common"})}
                            optionFilterProp="children"
                            optionFilterProp="upperGroupSelect"
                            filterOption={(input, option) =>
                                option.children
                                    .toString()
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                            }
                            filterSort={(optionA, optionB) =>
                                optionA.children
                                    .toString()
                                    .toLowerCase()
                                    .localeCompare(optionB.children.toString().toLowerCase())
                            }
                        >
                            <SelectField.Option value="" optionFilterProp="upperGroupSelect_option">
                                {t("filters.all", {ns: "common"})}
                            </SelectField.Option>
                            {upperGroups.map(upperGroup => (
                                <SelectField.Option
                                    key={upperGroup.group}
                                    value={upperGroup.group}
                                    optionFilterProp="upperGroupSelect_option"
                                >
                                    {upperGroup.group}
                                </SelectField.Option>
                            ))}
                        </SelectField>
                    </Col>
                    <Col span={6}>
                        <h5>{t("fields.regionGroup", {ns: "common"})}</h5>
                        <SelectField
                            value={regionGroups}
                            onChange={(value, children) => {
                                setCurrentPage(1);
                                setRegionGroups(value);
                                setRegionGroupsNames(children);
                            }}
                            disabled={!city || upperGroup === ""}
                            notFoundContent={t("filters.dataNotFound", {ns: "common"})}
                            mode="multiple"
                            placeholder={t("filters.addFilter", {ns: "common"})}
                            optionFilterProp="children"
                            data-testid="regionGroupsSelect"
                            filterOption={(input, option) =>
                                option.children
                                    .toString()
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                            }
                            filterSort={(optionA, optionB) =>
                                optionA.children
                                    .toString()
                                    .toLowerCase()
                                    .localeCompare(optionB.children.toString().toLowerCase())
                            }
                        >
                            {upperGroups
                                .find(c => c.group == upperGroup)
                                ?.regiongroups.map(regionGroupItem => (
                                    <SelectField.Option
                                        key={regionGroupItem._id}
                                        value={regionGroupItem._id}
                                        data-testid="regionGroupsSelect_option"
                                    >
                                        {regionGroupItem.name}
                                    </SelectField.Option>
                                ))}
                        </SelectField>
                    </Col>
                    <Col span={6}>
                        <h5>{t("filters.search", {ns: "common"})}</h5>
                        <Input.Search
                            data-testid="searchInput"
                            value={text}
                            onSearch={value => {
                                setCurrentPage(1);
                                setSearchText(value);
                            }}
                            onChange={event => {
                                setText(event.target.value);
                            }}
                        />
                    </Col>
                </FilterContainer>
            </Col>
            <SubCompanyContainer>
                <Table
                    rowKey="_id"
                    data-testid="subCompaniesTable"
                    dataSource={subCompanyData?.rows}
                    loading={subCompanyLoading}
                    pagination={{
                        total: subCompanyData.dataCount,
                        current: currentPage,
                        onChange: async page => {
                            setCurrentPage(page);
                            history.push(
                                "/companies/proof-of-delivery/" + companyId + "?page=" + page
                            );
                        },
                        hideOnSinglePage: true,
                        showSizeChanger: false
                    }}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                >
                    <Column
                        dataIndex="name"
                        title={t("fields.subcompany", {ns: "common"})}
                        render={text => <span data-textid="name">{text}</span>}
                    />
                    <Column
                        dataIndex="city"
                        title={t("fields.city", {ns: "common"})}
                        render={text => <span data-textid="city">{text}</span>}
                    />
                    <Column
                        dataIndex={"upperRegionGroup"}
                        title={t("fields.upperGroup", {ns: "common"})}
                        render={text => <span data-textid="upperRegionGroup">{text}</span>}
                    />
                    <Column
                        dataIndex={"regionGroup"}
                        title={t("fields.regionGroup", {ns: "common"})}
                        render={text => text?.name}
                        data-testid="regionGroup"
                    />
                    <Column
                        dataIndex="isActive"
                        shouldCellUpdate={() => {
                            return true;
                        }}
                        title={t("proofOfDelivery.fields.proofOfDelivery.short", {
                            ns: "proofOfDelivery"
                        })}
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="podIsActive"
                                    checked={subCompanyData.rows[index]?.options?.proof?.isActive}
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.proof.isActive = e;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="requiredStep"
                        shouldCellUpdate={() => {
                            return true;
                        }}
                        title={t("proofOfDelivery.fields.requiredStep", {ns: "proofOfDelivery"})}
                        render={(text, record, index) => {
                            return (
                                <Radio.Group
                                    key={index}
                                    data-testid="requiredStepRadioGroup"
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.proof.requiredStep = e.target.value;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                    value={subCompanyData.rows[index]?.options?.proof?.requiredStep}
                                >
                                    <Radio value={"preTrip"} data-testid="requiredStepRadio">
                                        {t("other.pickup", {ns: "common"})}
                                    </Radio>
                                    <Radio value={"postTrip"} data-testid="requiredStepRadio">
                                        {t("other.delivery", {ns: "common"})}
                                    </Radio>
                                    <Radio value={"both"} data-testid="requiredStepRadio">
                                        {t("filters.all", {ns: "common"})}
                                    </Radio>
                                </Radio.Group>
                            );
                        }}
                    />
                    <Column
                        dataIndex="requiredType"
                        shouldCellUpdate={() => {
                            return true;
                        }}
                        title={t("proofOfDelivery.fields.requiredType", {ns: "proofOfDelivery"})}
                        render={(text, record, index) => {
                            return (
                                <Radio.Group
                                    data-testid="requiredTypeRadioGroup"
                                    key={index}
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.proof.requiredType = e.target.value;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                    value={subCompanyData.rows[index]?.options?.proof?.requiredType}
                                >
                                    <Radio value={"delivery"} data-testid="requiredTypeRadio">
                                        {t("deliveryTypes.delivery", {ns: "common"})}
                                    </Radio>
                                    <Radio value={"return"} data-testid="requiredTypeRadio">
                                        {t("deliveryTypes.return", {ns: "common"})}
                                    </Radio>
                                    <Radio value={"both"} data-testid="requiredTypeRadio">
                                        {t("filters.all", {ns: "common"})}
                                    </Radio>
                                </Radio.Group>
                            );
                        }}
                    />
                    <Column
                        dataIndex="_id"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        render={(text, record, index) =>
                            showSubmitButton[text] && (
                                <Tooltip title={t("actions.save", {ns: "common"})}>
                                    <Button
                                        disabled={isSubCompanySubmitting}
                                        onClick={() => onSubCompanySubmit(index)}
                                        shape={"circle"}
                                        icon={<SaveOutlined />}
                                        type="primary"
                                        data-testid="submitButton"
                                    />
                                </Tooltip>
                            )
                        }
                    />
                </Table>
            </SubCompanyContainer>
        </div>
    );
};
